import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    AddressValidationState,
    IAuthorizedUser,
    ICreditProtection,
    IPersonalInfo,
    IPersonalInfoErrors,
    IPhoneAndIncome,
} from './models';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import useSessionTimeout from '../../hooks/useSessionTimeout';
import useAcquisitionsPreload from '../../hooks/useAcquisitionsPreload';
import {
    AcquisitionFlow,
    clearSessionAndRedirectToDataEntryPage,
    clearSessionStorageVariablesForAcquisitionFlow,
    getAcquisitionFlow,
    getDataEntryPages,
} from '../../utils/acquisition-utils';
import { initSkipPaApp, initSkipPqApp } from './applicationFetches';
import SessionStorage from '../../utils/SessionStorage';
import {
    acquisitionApiRequest,
    AcquisitionInitResponse,
} from '../../utils/acquisition-api/AcquisitionApi';

export interface IAuthorizedUserValidity {
    authorizedUserFirstName: boolean;
    authorizedUserLastName: boolean;
    authorizedUserDob: boolean;
    authorizedUserSpouse: boolean;
}
export interface IAcquisitionContext {
    // Authorized User
    authorizedUserInfo: IAuthorizedUser;
    setAuthorizedUserInfo: Dispatch<SetStateAction<IAuthorizedUser>>;
    isAuthorisedUserSectionValid: boolean;
    setIsAuthorisedUserSectionValid: Dispatch<SetStateAction<boolean>>;
    isAuthorizedUserInfoSaved: boolean;
    setIsAuthorizedUserInfoSaved: Dispatch<SetStateAction<boolean>>;
    isAuthorizedUserInEditMode: boolean;
    setIsAuthorizedUserInEditMode: Dispatch<SetStateAction<boolean>>;
    isAUSamePersonCheckDisabled: boolean;
    setIsAUSamePersonCheckDisabled: Dispatch<SetStateAction<boolean>>;
    isPersonalInfoInEditMode: boolean;
    setisPersonalInfoInEditMode: Dispatch<SetStateAction<boolean>>;
    isSamePersonError: boolean;
    setIsSamePersonError: Dispatch<SetStateAction<boolean>>;
    authorizedUserValidity: IAuthorizedUserValidity;
    setAuthorizedUserValidity: Dispatch<SetStateAction<IAuthorizedUserValidity>>;

    // Credit Protection
    creditProtectionInfo: ICreditProtection;
    setCreditProtectionInfo: Dispatch<SetStateAction<ICreditProtection>>;
    isCreditProtectionInfoValid: boolean;
    setIsCreditProtectionInfoValid: Dispatch<SetStateAction<boolean>>;
    setHasFocusedCreditProtection: Dispatch<SetStateAction<boolean>>;
    hasFocusedCreditProtection: boolean;
    checkIsCreditProtectionValid: () => boolean;

    // Ssn Module
    ssnInfo: string;
    setSsnInfo: Dispatch<SetStateAction<string>>;
    isSsnInfoValid: boolean;
    setIsSsnInfoValid: Dispatch<SetStateAction<boolean>>;

    // Personal Information
    personalInfo: any;
    setPersonalInfo: Dispatch<SetStateAction<any>>;
    isPersonalInfoValid: boolean;
    setIsPersonalInfoValid: Dispatch<SetStateAction<boolean>>;
    isEmailAutofillBuilt: boolean;
    setIsEmailAutofillBuilt: Dispatch<SetStateAction<boolean>>;
    isEmailBlurred: boolean;
    setIsEmailBlurred: Dispatch<SetStateAction<boolean>>;
    hideEmailMessage: boolean;
    setHideEmailMessage: Dispatch<SetStateAction<boolean>>;

    // Phone and Income
    phoneAndIncomeInfo: IPhoneAndIncome;
    setPhoneAndIncome: Dispatch<SetStateAction<IPhoneAndIncome>>;
    isPhoneAndIncomeValid: boolean;
    setIsPhoneAndIncomeValid: Dispatch<SetStateAction<boolean>>;
    isPhoneNumberValid: boolean;
    setIsPhoneNumberValid: Dispatch<SetStateAction<boolean>>;
    isMonthlyIncomeValid: boolean;
    setIsMonthlyIncomeValid: Dispatch<SetStateAction<boolean>>;

    // Funding Module
    fundingInfo: any;
    setFundingInfo: Dispatch<SetStateAction<any>>;
    isFundingSectionValid: boolean;
    setIsFundingSectionValid: Dispatch<SetStateAction<boolean>>;

    // Card Design
    selectedCardId: number;
    setSelectedCardId: Dispatch<SetStateAction<number>>;
    selectedCardValid: boolean;
    setSelectedCardValid: Dispatch<SetStateAction<boolean>>;

    // Application E-Sign
    isEsignAgreed: boolean;
    setIsEsignAgreed: Dispatch<SetStateAction<boolean>>;
    isEsignRequired: boolean;
    setIsEsignRequired: Dispatch<SetStateAction<boolean>>;
    isEsignShowing: boolean;
    setIsEsignShowing: Dispatch<SetStateAction<boolean>>;
    isEsignLinkValid: boolean;
    setIsEsignLinkValid: Dispatch<SetStateAction<boolean>>;
    esignVersion: string | undefined;
    setEsignVersion: Dispatch<SetStateAction<string | undefined>>;

    // Terms and Conditions
    isTermsValid: boolean;
    setIsTermsValid: Dispatch<SetStateAction<boolean>>;
    isTermsAgreed: boolean;
    setIsTermsAgreed: Dispatch<SetStateAction<boolean>>;

    // Consumer Report
    isConsumerReportAgreed: boolean;
    setIsConsumerReportAgreed: Dispatch<SetStateAction<boolean>>;

    // Balance Transfer
    balanceTransferSelected: boolean;
    setBalanceTransferSelected: Dispatch<SetStateAction<boolean>>;

    // Application CTA
    canFullAppContinue: boolean;

    // Personal Information Form
    personalInfoForm: IPersonalInfo;
    setPersonalInfoForm: Dispatch<SetStateAction<IPersonalInfo>>;
    personalInfoErrors: IPersonalInfoErrors;
    setPersonalInfoErrors: Dispatch<SetStateAction<IPersonalInfoErrors>>;

    isApplicationInfoRecieved: boolean;
    setIsApplicationInfoRecieved: Dispatch<SetStateAction<boolean>>;
    isPqOfOneGetComplete: boolean;
    setIsPqOfOneGetComplete: Dispatch<SetStateAction<boolean>>;
    isPartnerHostedGetComplete: boolean;
    setIsPartnerHostedGetComplete: Dispatch<SetStateAction<boolean>>;

    homeAddressValidateOnSubmit: boolean;
    setHomeAddressValidateOnSubmit: Dispatch<SetStateAction<boolean>>;

    hidingPlasticCardDesign: boolean; 
    setHidingPlasticCardDesign: Dispatch<SetStateAction<boolean>>;

    //Redundancy to store the InitResponse in the event that session storage gets cleared on iOS
    //This is in regards to the SkipPA 401 bug ticket DPM-7326
    initResponse: any;
    setInitResponse: Dispatch<SetStateAction<any>>;
    validatePersonalInfo: () => boolean;
    isMonthlyOrYearly: '' | 'monthly' | 'yearly';
    setIsMonthlyOrYearly: Dispatch<SetStateAction<'' | 'monthly' | 'yearly'>>;
    validationRef: React.RefObject<AddressValidationState>;

    //DPM-13680 Specific Flag
    homeAddressPrimaryRef: React.RefObject<any>;
}

export const AcquisitionContext = createContext({} as IAcquisitionContext);

export const useAcquisitionContext = () => useContext(AcquisitionContext);

export const AcquisitionContextProvider = (props: any): JSX.Element => {
    const [initResponse, setInitResponse] = useState<any>(null);
    const homeAddressPrimaryRef = useRef({ value: false });
    const [authorizedUserInfo, setAuthorizedUserInfo] =
        useState<IAuthorizedUser>({
            IsAuthorizedUserSelected: false,
            AuthorizedUserFirstName: '',
            AuthorizedUserMiddleInitial: '',
            AuthorizedUserLastName: '',
            AuthorizedUserDOBDay: '',
            AuthorizedUserDOBMonth: '',
            AuthorizedUserDOBYear: '',
        });
    const [isAuthorisedUserSectionValid, setIsAuthorisedUserSectionValid] =
        useState<boolean>(true);
    const [authorizedUserValidity, setAuthorizedUserValidity] = useState({
        authorizedUserFirstName: true,
        authorizedUserLastName: true,
        authorizedUserDob: true,
        authorizedUserSpouse: true,
    });

    const [isAuthorizedUserInfoSaved, setIsAuthorizedUserInfoSaved] =
        useState<boolean>(true);
    const [isAuthorizedUserInEditMode, setIsAuthorizedUserInEditMode] =
        useState<boolean>(false);
    const [isSamePersonError, setIsSamePersonError] = useState<boolean>(false);
    const [isAUSamePersonCheckDisabled, setIsAUSamePersonCheckDisabled] =
        useState<boolean>(false);
    const [isPersonalInfoInEditMode, setisPersonalInfoInEditMode] =
        useState<boolean>(false);

    const [isEmailAutofillBuilt, setIsEmailAutofillBuilt] = useState(false);
    const [isEmailBlurred, setIsEmailBlurred] = useState(false);
    const [hideEmailMessage, setHideEmailMessage] = useState(true);
    const [hidingPlasticCardDesign, setHidingPlasticCardDesign] = useState(false);

    const [creditProtectionInfo, setCreditProtectionInfo] =
        useState<ICreditProtection>({} as ICreditProtection);
    const [isCreditProtectionInfoValid, setIsCreditProtectionInfoValid] =
        useState<boolean>(true);
    const [hasFocusedCreditProtection, setHasFocusedCreditProtection] =
        useState<boolean>(false);
    const checkIsCreditProtectionValid = () => {
        return (
            !creditProtectionInfo.CreditProtectionSelected ||
            (creditProtectionInfo.CreditProtectionSelected &&
                creditProtectionInfo.CreditProtectionInitials.length >= 2)
        );
    };
    const pageNotContainingTerms = () => {
        return ([
            'pq-one/data-entry',
            'full-application/data-entry',
            'pre-qualification/application',
            'pre-qualification-result/counter-single-offer',
            ].every((path) => !window.location.href.includes(path)));
    }
    
    const [personalInfo, setPersonalInfo] = useState({});
    const [isPersonalInfoValid, setIsPersonalInfoValid] =
        useState<boolean>(true);

    const [ssnInfo, setSsnInfo] = useState<string>('');
    const [isSsnInfoValid, setIsSsnInfoValid] = useState<boolean>(true);

    const [phoneAndIncomeInfo, setPhoneAndIncome] = useState<IPhoneAndIncome>(
        {} as IPhoneAndIncome,
    );
    const [isPhoneAndIncomeValid, setIsPhoneAndIncomeValid] =
        useState<boolean>(true);
    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(true);
    const [isMonthlyIncomeValid, setIsMonthlyIncomeValid] =
        useState<boolean>(true);
    const [isMonthlyOrYearly, setIsMonthlyOrYearly] =
        useState<'' | 'monthly' | 'yearly'>('');

    const [fundingInfo, setFundingInfo] = useState({});
    const [isFundingSectionValid, setIsFundingSectionValid] =
        useState<boolean>(true);

    const [selectedCardId, setSelectedCardId] = useState<number>(0);
    const [selectedCardValid, setSelectedCardValid] = useState<boolean>(true);

    const [esignVersion, setEsignVersion] = useState<string | undefined>('');
    const [isEsignAgreed, setIsEsignAgreed] = useState<boolean>(false);
    const [isEsignShowing, setIsEsignShowing] = useState<boolean>(false);
    const [isEsignRequired, setIsEsignRequired] = useState<boolean>(false);
    const [isEsignLinkValid, setIsEsignLinkValid] = useState<boolean>(false);

    const [isTermsValid, setIsTermsValid] = useState<boolean>( pageNotContainingTerms() );
    const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false);

    const [isConsumerReportAgreed, setIsConsumerReportAgreed] =
        useState<boolean>(false);

    const [balanceTransferSelected, setBalanceTransferSelected] =
        useState<boolean>(false);

    const [canFullAppContinue, setCanFullAppContinue] =
        useState<boolean>(false);

    const [isApplicationInfoRecieved, setIsApplicationInfoRecieved] =
        useState<boolean>(false);

    const [isPqOfOneGetComplete, setIsPqOfOneGetComplete] =
        useState<boolean>(false);

    const [isPartnerHostedGetComplete, setIsPartnerHostedGetComplete] =
        useState<boolean>(false);

    const [homeAddressValidateOnSubmit, setHomeAddressValidateOnSubmit] =
        useState<boolean>(false);

    const validationRef = useRef({isValid: false, data: null});

    const isFlowValid = useAcquisitionsPreload();

    const checkIfCanContinue = (): boolean => {
        let canContinue = true;
        if (
            !isFundingSectionValid ||
            !isTermsAgreed ||
            !isConsumerReportAgreed
        ) {
            canContinue = false;
        }
        if (isEsignRequired && !isEsignAgreed) {
            canContinue = false;
        }
        return canContinue;
    };

    const [personalInfoForm, setPersonalInfoForm] = useState<IPersonalInfo>({
        firstName: { value: '', valid: true },
        middleInitial: { value: '', valid: true },
        lastName: { value: '', valid: true },
        suffix: { value: '', valid: true },
        mailingAddress: { value: '', valid: true },
        mailingAptUnit: { value: '', valid: true },
        mailingCity: { value: '', valid: true },
        mailingState: { value: '', valid: true },
        mailingZip: { value: '', valid: true },
        homeAddress: { value: '', valid: true },
        homeAptUnit: { value: '', valid: true },
        homeCity: { value: '', valid: true },
        homeState: { value: '', valid: true },
        homeZip: { value: '', valid: true },
        email: { value: '', valid: true },
        phoneNumber: { value: '', valid: true },
        ssn: { value: '', valid: true },
        monthlyIncome: { value: '', valid: true },
        dob: { value: '', valid: true },
        homeAddressShown: false,
    });

    const [personalInfoErrors, setPersonalInfoErrors] =
        useState<IPersonalInfoErrors>({
            FirstName: 'None',
            MiddleInitial: 'None',
            LastName: 'None',
            NameSuffix: 'None',
            MailingAddressLine1: 'None',
            MailingAddressLine2: 'None',
            MailingCity: 'None',
            MailingState: 'None',
            MailingZipCode: 'None',
            HomeAddressLine1: 'None',
            HomeAddressLine2: 'None',
            HomeCity: 'None',
            HomeState: 'None',
            HomeZipCode: 'None',
            EmailAddress: 'None',
            PrimaryPhoneNumber: 'None',
            Ssn: 'None',
            MonthlyIncome: 'None',
            DateOfBirth: 'None',
            Under19YearDOB: 'None',
            AddrAndAptNumMatch: 'None',
            HomeAddrAndAptNumMatch: 'None',
        });

    const validatePersonalInfo = useCallback(() => {
        if (
            getAcquisitionFlow() === AcquisitionFlow.PqOfOne ||
            getAcquisitionFlow() === AcquisitionFlow.PHA
        ) {
            return true;
        }
        return !Object.values(personalInfoErrors).some(
            fieldValue => fieldValue !== 'None',
        );
    }, [personalInfoErrors]);

    useEffect(() => {
        if (checkIfCanContinue()) {
            setCanFullAppContinue(true);
        } else {
            setCanFullAppContinue(false);
        }
    }, [
        isFundingSectionValid,
        isTermsAgreed,
        isConsumerReportAgreed,
        isEsignRequired,
        isEsignAgreed,
    ]);

    useEffect(() => {
        if (isFlowValid) {
            const acquisitionFlow = getAcquisitionFlow();
            if (
                (!isPqOfOneGetComplete &&
                    acquisitionFlow === AcquisitionFlow.PqOfOne) ||
                (!isPartnerHostedGetComplete &&
                    acquisitionFlow === AcquisitionFlow.PHA)
            ) {
                return;
            }

            if (acquisitionFlow === AcquisitionFlow.PHA) {
                setIsApplicationInfoRecieved(true);
                return;
            }

            if (PathUtils.getMetaPropertyValue('isSkipPqDataEntry')) {
                initSkipPqApp()
                    .then(isInitialized => {
                        if (isInitialized) {
                            setIsApplicationInfoRecieved(true);
                        }
                    })
                    .catch(err => {
                        console.debug('error getting application info', err);
                        clearSessionStorageVariablesForAcquisitionFlow();
                        window.location.reload();
                    });
            } else if (
                window.location.pathname.includes(
                    '/pre-approved/application/application-fulfillment',
                ) &&
                !window.location.pathname.includes(
                    'application-fulfillment-verification',
                ) &&
                !SessionStorage.verifyNotEmpty('pre-approval-data') &&
                window.location.port !== '4502'
            ) {
                const getInitData = async () => {
                    if (SessionStorage.verify('initResponse')) {
                        return SessionStorage.getObject('initResponse');
                    }

                    const { data } =
                        await acquisitionApiRequest<AcquisitionInitResponse>(
                            'init-compare-api',
                            {},
                        );
                    SessionStorage.setObject('initResponse', data);

                    return data;
                };

                getInitData()
                    .then(data => {
                        setInitResponse(data);
                        return initSkipPaApp(data);
                    })
                    .then(() => {
                        setIsApplicationInfoRecieved(true);
                    })
                    .catch((err: { errCode: string; message: string }) => {
                        const params = new URLSearchParams(
                            window.location.search,
                        );
                        params.append('ApcError', err.errCode);
                        params.append('errorCode', err.errCode);
                        window.location.assign(
                            getDataEntryPages().preApprovalDataEntryPageUrl +
                            '?' +
                            params.toString(),
                        );
                    });
            } else if (!window.location.href.includes('pq-one')) {
                setIsApplicationInfoRecieved(true);
            }
            if (PathUtils.getMetaPropertyValue('isPreApprovedFlow')) {
                setIsTermsAgreed(true);
            }
        } else if (
            ['ID-verify', 'Mitek-Flow', 'instructions'].includes(
                SessionStorage.get('lastVisitedPage'),
            ) ||
            SessionStorage.contains('mitekConfig')
        ) {
            clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Mitek history and config found');
        } else if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
            clearSessionAndRedirectToDataEntryPage('InvalidPartnerId');
        } else {
            clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Unsupported preload behavior');
        }
    }, [isPqOfOneGetComplete, isPartnerHostedGetComplete]);

    useEffect(() => {
        // PQ does not use this provider so it should not be initialized to required field
        if (
            getAcquisitionFlow() !== AcquisitionFlow.PQ &&
            !SessionStorage.verify('isCounterOffer')
        ) {
            setPersonalInfoErrors(prev => ({
                ...prev,
                MailingAddressLine1: 'RequiredField',
                MailingCity: 'RequiredField',
                MailingState: 'RequiredField',
                MailingZipCode: 'RequiredField',
            }));
        }
    }, []);

    const flow = getAcquisitionFlow();
    const refToPage = useRef(document.body);
    useSessionTimeout(refToPage, flow);

    return (
        <AcquisitionContext.Provider
            value={{
                authorizedUserInfo,
                setAuthorizedUserInfo,
                isAuthorisedUserSectionValid,
                setIsAuthorisedUserSectionValid,
                authorizedUserValidity,
                setAuthorizedUserValidity,
                isAUSamePersonCheckDisabled,
                setIsAUSamePersonCheckDisabled,
                isPersonalInfoInEditMode,
                setisPersonalInfoInEditMode,
                isAuthorizedUserInfoSaved,
                setIsAuthorizedUserInfoSaved,
                creditProtectionInfo,
                setCreditProtectionInfo,
                isCreditProtectionInfoValid,
                setIsCreditProtectionInfoValid,
                hasFocusedCreditProtection,
                setHasFocusedCreditProtection,
                checkIsCreditProtectionValid,
                ssnInfo,
                setSsnInfo,
                isSsnInfoValid,
                setIsSsnInfoValid,
                personalInfo,
                setPersonalInfo,
                isPersonalInfoValid,
                setIsPersonalInfoValid,
                phoneAndIncomeInfo,
                setPhoneAndIncome,
                validatePersonalInfo,
                isPhoneAndIncomeValid,
                setIsPhoneAndIncomeValid,
                isPhoneNumberValid,
                setIsPhoneNumberValid,
                isMonthlyIncomeValid,
                setIsMonthlyIncomeValid,
                isMonthlyOrYearly,
                setIsMonthlyOrYearly,
                fundingInfo,
                setFundingInfo,
                isFundingSectionValid,
                setIsFundingSectionValid,
                selectedCardId,
                setSelectedCardId,
                selectedCardValid,
                setSelectedCardValid,
                isEsignAgreed,
                setIsEsignAgreed,
                isEsignRequired,
                setIsEsignRequired,
                isEsignLinkValid,
                setIsEsignLinkValid,
                isTermsValid,
                setIsTermsValid,
                isTermsAgreed,
                setIsTermsAgreed,
                isConsumerReportAgreed,
                setIsConsumerReportAgreed,
                balanceTransferSelected,
                setBalanceTransferSelected,
                canFullAppContinue,
                personalInfoForm,
                setPersonalInfoForm,
                isApplicationInfoRecieved,
                setIsApplicationInfoRecieved,
                isPqOfOneGetComplete,
                setIsPqOfOneGetComplete,
                isPartnerHostedGetComplete,
                setIsPartnerHostedGetComplete,
                isEsignShowing,
                setIsEsignShowing,
                esignVersion,
                setEsignVersion,
                personalInfoErrors,
                setPersonalInfoErrors,
                homeAddressValidateOnSubmit,
                setHomeAddressValidateOnSubmit,
                isAuthorizedUserInEditMode,
                setIsAuthorizedUserInEditMode,
                isSamePersonError,
                setIsSamePersonError,
                initResponse,
                setInitResponse,
                isEmailAutofillBuilt,
                setIsEmailAutofillBuilt,
                isEmailBlurred,
                setIsEmailBlurred,
                hideEmailMessage,
                setHideEmailMessage,
                validationRef,
                hidingPlasticCardDesign,
                setHidingPlasticCardDesign,
                homeAddressPrimaryRef
            }}
        >
            {props.children}
        </AcquisitionContext.Provider>
    );
};
